import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import DragIndicatorRounded from "@mui/icons-material/DragIndicatorRounded";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { Stack } from "@mui/material";
import {
  DocumentPreviewDialog,
  FileListItem,
  formatFileSize,
  isPdfContentType,
  useFilePreviewWithAlert,
} from "@shared";

import { AssetInventoryDocumentResponse, DocumentGroup } from "@/api";
import { DocumentSelectCheckbox } from "@/common/components/layout/components/dropzone/components/document-select-checkbox/DocumentSelectCheckbox";
import { RenameInput } from "@/common/components/rename-input/RenameInput";
import { useRenameInput } from "@/common/components/rename-input/hooks/useRenameInput";
import { DraggableDocumentRow } from "@/pages/clients/client/components/tabs/debt-settlement-tab/tabs/asset-inventory-tab/components/draggable-document-row/DraggableDocumentRow";
import { AssetInventoryDocumentActions } from "@/pages/clients/client/components/tabs/debt-settlement-tab/tabs/asset-inventory-tab/hooks/useAssetInventoryDocumentActions";
import { useTranslation } from "@/translations";

import { toDocumentResponse } from "./AssetInventoryDocumentRow.utils";

const DOCUMENTS_SECTION = "clients.client.debtSettlement.assetInventory.documents";

interface Props {
  document: AssetInventoryDocumentResponse;
  sourceZone: DocumentGroup;
  actions: AssetInventoryDocumentActions;
  secondaryText?: string;
  menuAriaLabel?: string;
  selected?: boolean;
  /** When provided, the row shows a leading multi-select checkbox. */
  onToggleSelect?: (documentId: string) => void;
  dense?: boolean;
}

/**
 * Draggable asset-inventory document row with the DropZone-parity kebab menu plus the
 * Vermögensverzeichnis-specific read state: preview, mark as read/unread, rename (inline),
 * download, create case note, convert to PDF, and confirm-then-delete. Optionally multi-selectable.
 */
export function AssetInventoryDocumentRow({
  document,
  sourceZone,
  actions,
  secondaryText,
  menuAriaLabel,
  selected = false,
  onToggleSelect,
  dense = false,
}: Props) {
  const { t } = useTranslation();

  const rename = useRenameInput({
    fileName: document.fileName,
    onRename: (newFileName) => actions.rename(document.documentId, newFileName, document.fileName),
  });

  const preview = useFilePreviewWithAlert({ fetchBlob: () => actions.fetchPreviewBlob(document.documentId) });

  const readLabel = t(`${DOCUMENTS_SECTION}.readIndicator`);
  const secondaryParts = [
    dense ? formatFileSize(document.fileSizeBytes) : undefined,
    secondaryText,
    document.isRead ? readLabel : undefined,
  ].filter((part) => part !== undefined && part !== "");
  const resolvedSecondaryText = secondaryParts.length > 0 ? secondaryParts.join(" · ") : undefined;

  const fileListItem = (
    <FileListItem
      fileName={document.fileName}
      contentType={dense ? undefined : document.contentType}
      sizeBytes={dense ? undefined : document.fileSizeBytes}
      secondaryText={resolvedSecondaryText}
      dense={dense}
      leadingIcon={document.isRead ? <CheckCircleOutlineIcon color="success" titleAccess={readLabel} /> : undefined}
      primarySlot={
        rename.isEditing ? (
          <Stack sx={{ overflow: "hidden", flex: 1 }}>
            <RenameInput
              editValue={rename.editValue}
              setEditValue={rename.setEditValue}
              inputRef={rename.inputRef}
              extension={rename.extension}
              confirmRename={rename.confirmRename}
              handleKeyDown={rename.handleKeyDown}
            />
          </Stack>
        ) : undefined
      }
      menuAriaLabel={menuAriaLabel}
      isBusy={actions.isBusy}
      onPreview={preview.openPreview}
      onRename={rename.startEditing}
      onDownload={() => actions.download(document.documentId, document.fileName)}
      onCreateCaseNote={() => actions.createCaseNote(document.documentId, document.fileName)}
      onConvertToPdf={
        isPdfContentType(document.contentType)
          ? undefined
          : () => actions.convertToPdf(document.documentId, document.fileName)
      }
      onDelete={() => actions.requestDelete(document.documentId, document.fileName)}
      menuItems={[
        {
          key: "toggle-read",
          label: t(document.isRead ? `${DOCUMENTS_SECTION}.markAsUnread` : `${DOCUMENTS_SECTION}.markAsRead`),
          icon: document.isRead ? <RadioButtonUncheckedIcon /> : <CheckCircleOutlineIcon />,
          onClick: () => actions.markRead(document.documentId, !document.isRead),
          index: 1,
        },
      ]}
    />
  );

  return (
    <DraggableDocumentRow
      file={toDocumentResponse(document)}
      sourceZone={sourceZone}
      disabled={rename.isEditing || preview.isPreviewOpen}
      onActivate={preview.openPreview}
    >
      <Stack direction="row" alignItems="center" gap={0.5} sx={{ width: "100%" }}>
        {dense && <DragIndicatorRounded color="action" />}
        {onToggleSelect && (
          <DocumentSelectCheckbox
            fileName={document.fileName}
            checked={selected}
            onToggle={() => onToggleSelect(document.documentId)}
          />
        )}
        <Stack sx={{ overflow: "hidden", flex: 1 }}>{fileListItem}</Stack>
      </Stack>
      {preview.isPreviewOpen && (
        <DocumentPreviewDialog
          blob={preview.previewBlob}
          isLoading={preview.isPreviewLoading}
          fileName={document.fileName}
          uploadedAt={document.createdAtUtc}
          onClose={preview.closePreview}
        />
      )}
    </DraggableDocumentRow>
  );
}
